import { useState, type FormEvent } from 'react';

import {
  authenticateIdentity,
  openSession,
  type Branch,
  type Session,
} from '../api';
import {
  Boton,
  Fallo,
  Rotulo,
} from '../componentes';
import { LogoAnimado } from '../logo/LogoAnimado';

/**
 * Entrar al almacén.
 *
 * En dos pasos, como el POS: primero quién eres y después en qué local. No es
 * un trámite de más — un encargado de cadena tiene tres locales y **el almacén
 * es de uno**. Sin elegirlo, entraría al primero de la lista y movería el stock
 * del local equivocado, que es un descuadre en dos sitios a la vez.
 *
 * Con correo y contraseña, no con PIN. El PIN es para el salón, donde se entra
 * cuarenta veces por turno delante de todo el mundo; aquí se ven costos de
 * compra y condiciones de proveedores.
 */
/**
 * El negocio con el que se entró bien la última vez en este navegador.
 *
 * Venía escrito «lucqa» de fábrica —el de las pruebas—, y cada negocio tenía
 * que borrarlo antes de poner el suyo. Se recuerda solo el identificador corto,
 * que no es secreto; nunca el correo ni la contraseña.
 *
 * La clave se llama todavía `almacen.restaurante`, de cuando solo había
 * restaurantes: cambiarla haría que todos los equipos olvidaran su negocio.
 */
const NEGOCIO_RECORDADO = 'almacen.restaurante';

function negocioRecordado(): string {
  try {
    return window.localStorage.getItem(NEGOCIO_RECORDADO) ?? '';
  } catch {
    return '';
  }
}

function recordarNegocio(slug: string): void {
  try {
    window.localStorage.setItem(NEGOCIO_RECORDADO, slug);
  } catch {
    // Sin almacenamiento —ventana privada— se teclea cada vez, y funciona igual.
  }
}

/**
 * Para quién es, a la vista antes de teclear nada: quien llega de una botica
 * se reconoce en la lista, y no tiene que adivinar si entró al programa de un
 * restaurante.
 */
const RUBROS_DE_LA_ENTRADA = [
  'Restaurantes',
  'Bodegas',
  'Ropa',
  'Boticas',
  'Ferreterías',
  'Tecnología',
] as const;

export function Entrada({
  onEntra,
  aviso = null,
}: {
  onEntra: (session: Session) => void;
  /** Por qué se está aquí, si se llegó con un código del celular que no sirvió. */
  aviso?: unknown;
}) {
  const [tenantSlug, setTenantSlug] = useState(negocioRecordado);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const [branches, setBranches] = useState<Branch[] | null>(null);
  const [bootstrapToken, setBootstrapToken] = useState('');
  const [branchId, setBranchId] = useState('');

  const [error, setError] = useState<unknown>(aviso);
  const [ocupado, setOcupado] = useState(false);

  async function identificarse(evento: FormEvent) {
    evento.preventDefault();
    setError(null);
    setOcupado(true);

    try {
      // Como se guarda: en minúsculas y sin espacios. «Casa-Violeta » tecleado
      // en la tablet no encontraba a nadie.
      const slug = tenantSlug.trim().toLowerCase();
      const identidad = await authenticateIdentity({
        email,
        password,
        tenantSlug: slug,
      });
      recordarNegocio(slug);

      setBootstrapToken(identidad.bootstrapToken);
      setBranches(identidad.branches);
      setBranchId(identidad.branches[0]?.branchId ?? '');

      // Con un solo local no hay nada que elegir: preguntarlo sería un paso de
      // peaje para llegar al único sitio posible.
      if (identidad.branches.length === 1 && identidad.branches[0]) {
        onEntra(
          await openSession(identidad.bootstrapToken, identidad.branches[0].branchId),
        );
      }
    } catch (fallo) {
      setError(fallo);
    } finally {
      setOcupado(false);
    }
  }

  async function elegirLocal(evento: FormEvent) {
    evento.preventDefault();
    setError(null);
    setOcupado(true);

    try {
      onEntra(await openSession(bootstrapToken, branchId));
    } catch (fallo) {
      setError(fallo);
    } finally {
      setOcupado(false);
    }
  }

  return (
    <div className="entrada">
      <div className="entrada__formulario">
        <div className="entrada__caja">
          {/*
           * El escudo animado encima del formulario. Es el mismo que abre el
           * POS: que sea la misma marca que la caja le dice a quien recibe
           * mercadería en la puerta que está en el sitio correcto antes de
           * teclear nada.
           */}
          <LogoAnimado className="entrada__escudo" />
          <p className="entrada__logotipo">
            lucqa <span className="entrada__cara">Almacén</span>
          </p>
          {/*
           * Para cualquier negocio. Decía «Insumos… del local» y pedía el
           * «Restaurante» con «mi-restaurante» de ejemplo, también a la botica
           * y a la ferretería: lo primero que leían era que esto no era suyo.
           */}
          <p className="entrada__frase">
            Lo que tienes, lo que entra y lo que sale de tu negocio, desde
            cualquier equipo.
          </p>
          <ul className="entrada__rubros" aria-label="Para qué negocios es">
            {RUBROS_DE_LA_ENTRADA.map((rubro) => (
              <li key={rubro}>{rubro}</li>
            ))}
          </ul>

          {branches === null ? (
            <form onSubmit={identificarse}>
              <h1>Entrar al almacén</h1>
              <p>Con el correo y la contraseña con los que administras tu negocio.</p>

              <div className="campo">
                <Rotulo htmlFor="negocio" ayuda="El nombre corto de tu negocio en Lucqa, en minúsculas y con guiones: el que te dimos al darte de alta.">Tu negocio</Rotulo>
                <input
                  id="negocio"
                  placeholder="nombre-de-tu-negocio"
                  autoCapitalize="none"
                  spellCheck={false}
                  value={tenantSlug}
                  onChange={(e) => setTenantSlug(e.target.value)}
                  autoComplete="organization"
                  required
                />
              </div>

              <div className="campo">
                <Rotulo htmlFor="correo" ayuda="El correo con el que te dieron acceso.">Correo</Rotulo>
                <input
                  id="correo"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="username"
                  required
                />
              </div>

              <div className="campo">
                <Rotulo htmlFor="clave" ayuda="Tu contraseña. Si la olvidaste, pídele a quien administra el local que te la cambie.">Contraseña</Rotulo>
                <input
                  id="clave"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                />
              </div>

              {error === null ? null : <Fallo error={error} />}

              <Boton variante="primario" type="submit" disabled={ocupado}>
                {ocupado ? 'Entrando…' : 'Continuar'}
              </Boton>
            </form>
          ) : (
            <form onSubmit={elegirLocal}>
              <h1>¿Qué local?</h1>
              <p>El almacén es de un local: lo que se mueva aquí sale de su estante.</p>

              <div className="campo">
                <Rotulo htmlFor="local" ayuda="El local cuyo almacén vas a mover. Lo que hagas afecta solo a ese local.">Local</Rotulo>
                <select
                  id="local"
                  value={branchId}
                  onChange={(e) => setBranchId(e.target.value)}
                >
                  {branches.map((branch) => (
                    <option key={branch.branchId} value={branch.branchId}>
                      {branch.companyTradeName} · {branch.branchName}
                    </option>
                  ))}
                </select>
              </div>

              {error === null ? null : <Fallo error={error} />}

              <Boton variante="primario" type="submit" disabled={ocupado}>
                {ocupado ? 'Abriendo…' : 'Entrar al almacén'}
              </Boton>
            </form>
          )}
        </div>
      </div>

      {/*
       * La ilustración, a la derecha y solo de adorno.
       *
       * `alt` vacío a propósito: no dice nada que el formulario no diga ya, y
       * un lector de pantalla que la describa solo retrasa a quien viene a
       * teclear su correo.
       *
       * En WebP y a 900 de ancho: el original pesaba 1,7 MB, que es mucho para
       * lo primero que carga alguien con la tablet en el almacén y el wifi del
       * fondo del local.
       */}
      <aside className="entrada__ilustracion">
        <img
          src="/login-almacen.webp"
          alt=""
          width={900}
          height={1125}
          loading="eager"
        />
      </aside>
    </div>
  );
}